<template>
  <v-container>
    <v-row>
      <v-col>
        <v-card flat>
          <v-card-title> Agents </v-card-title>
          <v-card-text>
            <v-row class="mb-3">
              <v-col cols="12" md="6" class="px-6">
                <v-text-field
                  class="py-0"
                  v-model="search"
                  label="Search"
                  prepend-inner-icon="mdi-magnify"
                  single-line
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="px-6 d-flex align-center justify-end">
                <ExportCSV :data="this.csvData" :name="'agent-testing.csv'">
                  <v-btn color="primary" outlined>
                    <v-icon left small>mdi-download</v-icon>
                    Export to CSV
                  </v-btn>
                </ExportCSV>
              </v-col>
            </v-row>
            <v-data-table
              :loading="load"
              :headers="headers"
              :items="reportData"
              :search="search"
              sort-by="date"
              :sort-desc="true"
              multi-sort
              loading-text="Loading data..."
              class="elevation-0 col-md-12 m-auto"
            >
              <template v-slot:item.agentID="{ item }">
                <agent-modal
                  :agent="item"
                  v-if="
                    user.role === 'superuser' ||
                    user.role === 'manager' ||
                    user.role === 'fmo'
                  "
                ></agent-modal>

                <span v-else>{{ item.agentID }}</span>
              </template>

              <template v-slot:item.fname="{ item }">
                {{ item.agentType === 'GeneralAgent' ? (item.legalEntityName || '') : (item.fname || '') }}
              </template>

              <template v-slot:item.lname="{ item }">
                {{ item.agentType === 'GeneralAgent' ? '' : (item.lname || '') }}
              </template>

              <template v-slot:item.roster="{ item }">
                {{ item.roster.length }}
              </template>

              <template v-slot:item.trainingComplete="{ item }">
                <v-icon :color="colorIcon(item.trainingComplete)">
                  {{ item.trainingComplete ? "mdi-check" : "mdi-close" }}
                </v-icon>
              </template>

              <template v-slot:item.result="{ item }">
                <v-icon
                  @click="openResults(item)"
                  :color="colorIcon(item.result)"
                >
                  {{ item.result === "Pass" ? "mdi-check" : "mdi-close" }}
                </v-icon>
              </template>

              <template v-slot:item.ACTIONS="{ item }">
                <div class="d-flex align-center" style="gap: 8px;">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon 
                        small 
                        v-bind="attrs"
                        v-on="on"
                        @click="openEditDialog(item)"
                        class="action-icon"
                      >
                        mdi-pencil
                      </v-icon>
                    </template>
                    <span>Edit Agent</span>
                  </v-tooltip>
                  
                  <v-tooltip 
                    v-if="(item.agreementStatus === 'Pending' || !item.agreementStatus) && (user.role === 'superuser' || user.role === 'manager')"
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="success"
                        v-bind="attrs"
                        v-on="on"
                        @click="approveAgent(item)"
                        class="action-icon"
                      >
                        mdi-check-circle
                      </v-icon>
                    </template>
                    <span>Approve Agent (Send Contract for Signature)</span>
                  </v-tooltip>
                  
                  <v-tooltip 
                    v-if="(item.agreementStatus === 'Pending' || !item.agreementStatus) && (user.role === 'superuser' || user.role === 'manager')"
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        @click="rejectAgent(item)"
                        class="action-icon"
                      >
                        mdi-close-circle
                      </v-icon>
                    </template>
                    <span>Reject Agent</span>
                  </v-tooltip>
                  
                  <v-tooltip 
                    v-if="item.agentType === 'GeneralAgent' && (user.role === 'superuser' || user.role === 'manager')"
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="openCommissionDialog(item)"
                        class="action-icon"
                      >
                        mdi-cash-multiple
                      </v-icon>
                    </template>
                    <span>Edit Commission Rates</span>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:item.agreementStatus="{ item }">
                <v-chip
                  :color="getStatusColor(item.agreementStatus)"
                  small
                  dark
                >
                  {{ getStatusText(item.agreementStatus) }}
                </v-chip>
              </template>

            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="editDialog" max-width="450px">
      <template>
        <v-form ref="editForm" v-model="editValid" lazy-validation>
          <v-card>
            <v-card-title> Edit Agent </v-card-title>

            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-autocomplete
                      label="Search FMO Name"
                      v-model="agentToEdit.agentFmo"
                      :items="fmoList"
                      :loading="fmoListLoading"
                      :search-input.sync="fmoSearch"
                      item-text="NAME"
                      return-object
                      clearable
                      outlined
                      dense
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-title>
                            Search for FMO
                          </v-list-item-title>
                        </v-list-item>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ item.NAME }}</v-list-item-title
                          >
                          <v-list-item-subtitle>{{
                            item.NPN
                          }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="closeEditDialog" text>Cancel</v-btn>
              <v-btn
                @click="validateEdits"
                :disabled="!editValid"
                color="accent"
                text
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </template>
    </v-dialog>

    <!-- Commission Rates Dialog -->
    <v-dialog v-model="commissionDialog" max-width="600px">
      <v-card>
        <v-card-title>
          Edit Commission Rates - {{ commissionAgent.legalEntityName || commissionAgent.fname || commissionAgent.agentID }}
        </v-card-title>
        <v-card-text>
          <v-form ref="commissionForm" v-model="commissionValid">
            <v-text-field
              v-model="commissionData.agencyNpn"
              label="Agency NPN (National Producer Number)"
              outlined
              dense
              class="mb-2"
            ></v-text-field>

            <v-text-field
              v-model="commissionData.commissionRate"
              label="Commission Rate (%)"
              outlined
              dense
              type="number"
              step="0.01"
              min="0"
              class="mb-2"
            ></v-text-field>

            <v-text-field
              v-model="commissionData.newEnrollmentRate"
              label="New Enrollment Rate (%)"
              outlined
              dense
              type="number"
              step="0.01"
              min="0"
              class="mb-2"
            ></v-text-field>

            <v-text-field
              v-model="commissionData.renewalRate"
              label="Renewal Rate (%)"
              outlined
              dense
              type="number"
              step="0.01"
              min="0"
              class="mb-2"
            ></v-text-field>

            <v-text-field
              v-model="commissionData.planToPlanRate"
              label="Plan to Plan Rate (%)"
              outlined
              dense
              type="number"
              step="0.01"
              min="0"
              class="mb-2"
            ></v-text-field>

            <v-divider class="my-4"></v-divider>
            <v-subheader>Residual Compensation (Monthly)</v-subheader>

            <v-text-field
              v-model="commissionData.renewalRate"
              label="Residual Rate - Tier 1 ($ per month per subscription)"
              outlined
              dense
              type="number"
              step="0.01"
              min="0"
              hint="For subscriptions 1 - 4,999"
              persistent-hint
              class="mb-2"
            ></v-text-field>

            <v-text-field
              v-model="commissionData.renewalRateTier2"
              label="Residual Rate - Tier 2 ($ per month per subscription)"
              outlined
              dense
              type="number"
              step="0.01"
              min="0"
              hint="Starting at threshold subscriptions"
              persistent-hint
              class="mb-2"
            ></v-text-field>

            <v-text-field
              v-model="commissionData.renewalRateThreshold"
              label="Tier 2 Threshold (number of subscriptions)"
              outlined
              dense
              type="number"
              min="0"
              hint="Tier 2 rate applies starting at this number of subscriptions"
              persistent-hint
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCommissionDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="commissionLoading"
            :disabled="!commissionValid"
            @click="saveCommissionRates"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="examResultsDialog" max-width="50%">
      <v-toolbar dark color="primary">
        <v-toolbar-title>Agent Exam Results</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click.stop="examResultsDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card tile>
        <v-card-text class="py-3">
          <v-row v-if="selectedAgentAnswers.length == 0">
            <v-col>
              <v-alert type="info">No exam results to display.</v-alert>
            </v-col>
          </v-row>
          <v-row
            v-else
            v-for="(question, qIndex) in this.quizData.questions"
            :key="question.question"
            justify="center"
          >
            <v-col cols="12">
              <v-card>
                <v-card-title>
                  {{ question.question }}
                </v-card-title>
                <v-card-text>
                  <v-radio-group v-model="selectedAgentAnswers[qIndex]">
                    <v-radio
                      v-for="(answer, index) in question.answers"
                      disabled
                      :key="answer"
                      :label="answer"
                      :value="index"
                      :active-class="
                        selectedAgentAnswers[qIndex] == question.correctAnswer
                          ? 'right-answer'
                          : 'wrong-answer'
                      "
                      :class="
                        index == question.correctAnswer
                          ? 'right-answer pa-3'
                          : 'pa-3'
                      "
                    ></v-radio>
                  </v-radio-group>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="saveSuccess" color="success" :timeout="6000">
      {{ successMessage || 'Changes have successfully been saved' }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="saveSuccess = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import AgentModal from "../components/AgentModal";
import ExportCSV from "vue-json-csv";
import quiz from "../assets/quiz.json";

export default {
  name: "Agents",
  components: {
    AgentModal,
    ExportCSV,
  },
  data() {
    return {
      load: false,
      search: "",
      quizData: quiz,
      examResultsDialog: false,
      selectedAgentAnswers: [],
      headers: [
        { text: "Actions", value: "ACTIONS" },
        { text: "First Name", value: "fname" },
        { text: "Last Name", value: "lname" },
        { text: "Phone Number", value: "phoneNumber" },
        { text: "Email", value: "agentEmail" },
        { text: "Agent ID", value: "agentID" },
        { text: "Status", value: "agreementStatus" },
        { text: "FMO", value: "fmoName" },
        {
          text: "Training Completed",
          value: "trainingComplete",
          align: "center",
        },
        { text: "Results", value: "result" },
        { text: "Num. Of Attempts", value: "attempts" },
        { text: "Date", value: "date" },
        { text: "Members Enrolled", value: "roster" },
      ],
      editValid: false,
      editDialog: false,
      editedIndex: -1,
      agentToEdit: {},
      fmoList: [],
      fmoListLoading: false,
      fmoSearch: null,
      saveSuccess: false,
      successMessage: '',
      reportData: [],
      csvData: [],
      fmoAgentIds: [],
      // Commission Rates
      commissionDialog: false,
      commissionValid: false,
      commissionLoading: false,
      commissionAgent: {},
      commissionData: {
        agencyNpn: "",
        commissionRate: "20.00",
        newEnrollmentRate: "20.00",
        renewalRate: "2.00",
        planToPlanRate: "20.00",
        renewalRateTier2: "3.00",
        renewalRateThreshold: 5000,
      },
    };
  },
  methods: {
    getHeaders() {
      if (this.user.role === "fmo") {
        this.headers = [
          { text: "First Name", value: "fname" },
          { text: "Last Name", value: "lname" },
          { text: "Phone Number", value: "phoneNumber" },
          { text: "Email", value: "agentEmail" },
          { text: "Agent ID", value: "agentID" },
          { text: "Status", value: "agreementStatus" },
          { text: "FMO", value: "fmoName" },
          {
            text: "Training Completed",
            value: "trainingComplete",
            align: "center",
          },
          { text: "Results", value: "result" },
          { text: "Num. Of Attempts", value: "attempts" },
          { text: "Date", value: "date" },
          { text: "Members Enrolled", value: "roster" },
        ];
      } else {
        this.headers = [
          { text: "Actions", value: "ACTIONS" },
          { text: "First Name", value: "fname" },
          { text: "Last Name", value: "lname" },
          { text: "Phone Number", value: "phoneNumber" },
          { text: "Email", value: "agentEmail" },
          { text: "Agent ID", value: "agentID" },
          { text: "Status", value: "agreementStatus" },
          { text: "FMO", value: "fmoName" },
          {
            text: "Training Completed",
            value: "trainingComplete",
            align: "center",
          },
          { text: "Results", value: "result" },
          { text: "Num. Of Attempts", value: "attempts" },
          { text: "Date", value: "date" },
          { text: "Members Enrolled", value: "roster" },
        ];
      }
    },
    getUser() {
      axios
        .get("/api/user")
        .then((response) => {
          this.user = response.data[0];
          // check role and reroute if unauthorized
          if (this.user.role === "agent") {
            this.$router.push("/");
          } else if (this.user.role === "hra") {
            this.$router.push("/hraenrollment");
          } else if (this.user.role === "credentialing") {
            this.$router.push("/credentialing");
          } else {
            this.getHeaders();
            this.getAgents();
          }
        })
        .catch(() => {
          this.$router.push("/login");
        });
    },
    async getAgents() {
      this.reportData = [];
      this.load = true;
      let agentIds;
      if (this.user.role === "fmo") {
        await this.getFmoAgents();
        agentIds = this.fmoAgentIds;
      } else {
        agentIds = "all";
      }
      await axios.get(`api/agents/all/${agentIds}`).then((res) => {
        this.reportData = res.data;
        // Make copy of report data so we can remove the _id field for exporting csv's
        this.csvData = JSON.parse(JSON.stringify(this.reportData));
        this.csvData.forEach((agent) => {
          delete agent._id;
          agent.roster = agent.roster.length;
        });
        this.load = false;
      });
    },
    openResults(agent) {
      if (agent.answers != null) {
        this.selectedAgentAnswers = agent.answers;
      } else {
        this.selectedAgentAnswers = [];
      }
      this.examResultsDialog = true;
    },
    openEditDialog(item) {
      this.editedIndex = this.reportData.indexOf(item);
      this.agentToEdit = Object.assign({}, item);
      this.fmoList = [{ NAME: item.fmoName }];
      this.agentToEdit.agentFmo = {
        NAME: item.fmoName,
      };
      this.editDialog = true;
    },
    validateEdits() {
      if (this.$refs.editForm.validate()) {
        this.saveEdits();
      }
    },
    async saveEdits() {
      let data = {
        fmoNpn: this.agentToEdit.agentFmo ? this.agentToEdit.agentFmo.NPN : "",
        fmoName: this.agentToEdit.agentFmo
          ? this.agentToEdit.agentFmo.NAME
          : "",
        agentID: this.agentToEdit.agentID,
      };
      await axios
        .post("/api/agents/update", data)
        .then((res) => {
          this.reportData[this.editedIndex].fmoName = res.data.fmoName;
          this.saveSuccess = true;
          this.closeEditDialog();
        })
        .catch((err) => {
          console.log(err);
          console.log("Couldn't update user.");
        });
    },
    closeEditDialog() {
      this.editDialog = false;
      this.agentToEdit = {};
      this.fmoList = [];
    },
    async getFmoAgents() {
      this.fmoAgentIds = [];
      await axios.get(`api/fmo/agents/${this.user.fmoNpn}`).then((res) => {
        this.fmoAgentIds = res.data;
      });
    },
    colorIcon(res) {
      if (res === "Pass" || res === true) {
        return "success";
      } else {
        return "red";
      }
    },
    getStatusColor(status) {
      const colors = {
        'Active': 'success',
        'Pending': 'warning',
        'PendingSignature': 'orange', // Laranja para aguardando assinatura
        'Terminated': 'error',
        'Expired': 'grey'
      };
      return colors[status] || 'warning'; // Default para 'Pending' se não especificado
    },
    getStatusText(status) {
      const texts = {
        'Active': 'Active',
        'Pending': 'Pending Approval',
        'PendingSignature': 'Pending Signature',
        'Terminated': 'Terminated',
        'Expired': 'Expired'
      };
      return texts[status] || 'Pending Approval';
    },
    async approveAgent(item) {
      if (!confirm(`Are you sure you want to approve ${item.legalEntityName || item.fname || item.agentID}?\n\nA contract will be sent via Adobe Sign for signature. The agent will be activated after the contract is signed.`)) {
        return;
      }

      try {
        const response = await axios.post(`/api/agents/approve/${item._id}`);
        this.successMessage = response.data.message || "Agent approved. Contract will be sent for signature via Adobe Sign.";
        this.saveSuccess = true;
        // Recarregar lista
        await this.getAgents();
      } catch (error) {
        console.error("Error approving agent:", error);
        alert(error.response?.data?.message || "Error approving agent");
      }
    },
    async rejectAgent(item) {
      const reason = prompt(`Enter reason for rejecting ${item.legalEntityName || item.fname || item.agentID}:`);
      if (!reason) {
        return;
      }

      try {
        await axios.post(`/api/agents/reject/${item._id}`, { reason });
        this.saveSuccess = true;
        // Recarregar lista
        await this.getAgents();
      } catch (error) {
        console.error("Error rejecting agent:", error);
        alert(error.response?.data?.message || "Error rejecting agent");
      }
    },
    openCommissionDialog(item) {
      this.commissionAgent = item;
      this.commissionData = {
        agencyNpn: item.agencyNpn || "",
        commissionRate: item.commissionRate || "20.00",
        newEnrollmentRate: item.newEnrollmentRate || "20.00",
        renewalRate: item.renewalRate || "2.00",
        planToPlanRate: item.planToPlanRate || "20.00",
        renewalRateTier2: item.renewalRateTier2 || "3.00",
        renewalRateThreshold: item.renewalRateThreshold || 5000,
      };
      this.commissionDialog = true;
    },
    closeCommissionDialog() {
      this.commissionDialog = false;
      this.commissionAgent = {};
      this.commissionData = {
        agencyNpn: "",
        commissionRate: "20.00",
        newEnrollmentRate: "20.00",
        renewalRate: "2.00",
        planToPlanRate: "20.00",
        renewalRateTier2: "3.00",
        renewalRateThreshold: 5000,
      };
      if (this.$refs.commissionForm) {
        this.$refs.commissionForm.reset();
      }
    },
    async saveCommissionRates() {
      if (!this.$refs.commissionForm.validate()) {
        return;
      }

      this.commissionLoading = true;

      try {
        await axios.put(
          `/api/agents/${this.commissionAgent._id}/commission-rates`,
          this.commissionData
        );

        this.saveSuccess = true;
        this.closeCommissionDialog();
        // Recarregar lista
        await this.getAgents();
      } catch (error) {
        console.error("Error saving commission rates:", error);
        alert(error.response?.data?.message || "Error saving commission rates");
      } finally {
        this.commissionLoading = false;
      }
    },
  },
  watch: {
    fmoSearch() {
      if (this.fmoSearch !== null) {
        this.fmoListLoading = true;

        // Lazily load input items
        axios.get(`/api/search/fmo/${this.fmoSearch[0]}`).then((response) => {
          this.fmoList = response.data;
          this.fmoListLoading = false;
        });
      }
    },
  },
  mounted() {
    this.getUser();
  },
};
</script>

<style scoped>
.v-card__title {
  word-break: break-word !important;
}

.action-icon {
  cursor: pointer;
  transition: opacity 0.2s;
}

.action-icon:hover {
  opacity: 0.7;
}

.right-answer {
  background-color: rgb(0, 200, 83, 0.5);
  border-radius: 5px;
}
.wrong-answer {
  background-color: rgba(255, 82, 82, 0.5);
  border-radius: 5px;
}
</style>
